import { useTranslation } from "@sagot/i18n";
import { Skeleton, StatusBadge, type StatusKind } from "@sagot/ui";
import { useFormatters } from "../shared";
import { useSalesRequests } from "./sales-request-dialog";

const STATUS_KIND: Readonly<Record<string, StatusKind>> = { new: "info", contacted: "warning", done: "success" };

/**
 * "Your sales requests": what this workspace asked sales for and where it stands (new, contacted,
 * done). Shown only once the workspace has made a request; a failed load stays out of the way
 * because the rest of Billing does not depend on it.
 */
export function SalesRequestsCard() {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const requests = useSalesRequests();
  if (requests.isError) return null;
  if (requests.isPending) return <Skeleton className="h-24" aria-hidden="true" />;
  if (requests.data.length === 0) return null;
  return (
    <section className="card" aria-labelledby="sr-history-title" data-testid="sales-requests-card">
      <div className="card-header">
        <h2 id="sr-history-title" className="card-title">
          {t("billing.salesRequest.history.title")}
        </h2>
      </div>
      <ul className="card-body divide-y divide-line">
        {requests.data.slice(0, 5).map((request) => (
          <li key={request.id} className="flex flex-wrap items-center justify-between gap-2 py-2 first:pt-0 last:pb-0">
            <div className="min-w-0">
              <div className="text-sm font-semibold">{t("billing.salesRequest.history.row", { number: request.number, plan: request.plan_key ? `${request.plan_key.charAt(0).toUpperCase()}${request.plan_key.slice(1)}` : t("billing.salesRequest.history.noPlan") })}</div>
              <div className="text-xs text-fg-muted">{fmt.date(request.created_at)}</div>
            </div>
            <StatusBadge status={STATUS_KIND[request.status] ?? "neutral"} label={t(`billing.salesRequest.history.status_${request.status}`, { defaultValue: request.status })} />
          </li>
        ))}
      </ul>
    </section>
  );
}
